import React, { useEffect, useRef, useState, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

declare global {
  interface Window {
    YT: any;
    onYouTubeIframeAPIReady: () => void;
  }
}

interface YouTubePlayerProps {
  videoId: string;
  onReady?: (player: any) => void;
  onStateChange?: (event: any) => void;
  onTimeUpdate?: (currentTime: number) => void;
  onSeek?: (time: number) => void;
  onError?: (error: string) => void;
  width?: number | string;
  height?: number;
  startTime?: number;
  endTime?: number;
  isLooping?: boolean;
  loopStartTime?: number;
  loopEndTime?: number;
  isFlipped?: boolean;
  playbackRate?: number;
}

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  videoId,
  onReady,
  onStateChange,
  onTimeUpdate,
  onSeek,
  onError,
  width = 640,
  height = 360,
  startTime = 0,
  endTime,
  isLooping = false,
  loopStartTime = 0,
  loopEndTime = 0,
  isFlipped = false,
  playbackRate = 1.0
}) => {
  const playerRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAPIReady, setIsAPIReady] = useState(false);
  const [isPlayerReady, setIsPlayerReady] = useState(false);
  const timeUpdateIntervalRef = useRef<NodeJS.Timeout | null>(null);

  // Load YouTube IFrame API
  useEffect(() => {
    console.log('🎬 Loading YouTube IFrame API, window.YT exists:', !!window.YT);
    if (window.YT) {
      console.log('🎬 YouTube API already loaded');
      setIsAPIReady(true);
      return;
    }

    console.log('🎬 Creating YouTube IFrame API script tag');
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    tag.onerror = () => {
      const errorMessage = 'Failed to load YouTube API';
      setIsLoading(false);
      setError(errorMessage);
      onError?.(errorMessage);
    };
    const firstScriptTag = document.getElementsByTagName('script')[0];
    if (firstScriptTag?.parentNode) {
      firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);
    } else {
      document.head.appendChild(tag);
    }

    window.onYouTubeIframeAPIReady = () => {
      console.log('🎬 YouTube IFrame API ready callback triggered');
      setIsAPIReady(true);
    };

    return () => {
      (window as any).onYouTubeIframeAPIReady = undefined;
    };
  }, [onError]);

  // Initialize player when API is ready
  useEffect(() => {
    if (!isAPIReady || !containerRef.current) return;
    if (!videoId) {
      const errorMessage = 'Invalid video ID';
      setIsLoading(false);
      setError(errorMessage);
      onError?.(errorMessage);
      return;
    }

    const initPlayer = () => {
      try {
        console.log('🎬 Initializing YouTube player for videoId:', videoId);
        playerRef.current = new window.YT.Player(containerRef.current, {
          height: height,
          width: width,
          videoId: videoId,
          playerVars: {
            start: startTime,
            end: endTime,
            controls: 1,
            modestbranding: 1,
            rel: 0,
            showinfo: 0
          },
          events: {
            onReady: (event: any) => {
              console.log('🎬 YouTube IFrame API onReady called:', event);
              setIsLoading(false);
              setIsPlayerReady(true);
              onReady?.(event.target);
            },
            onStateChange: (event: any) => {
              console.log('🎬 YouTube IFrame API onStateChange:', event.data, 'PlayerState:', window.YT?.PlayerState);
              onStateChange?.(event);
              
              // Handle looping
              if (isLooping && event.data === window.YT.PlayerState.ENDED) {
                const player = event.target;
                player.seekTo(loopStartTime);
                player.playVideo();
              }
            },
            onError: (event: any) => {
              setIsLoading(false);
              const errorMessage = `YouTube player error: ${event.data}`;
              setError(errorMessage);
              onError?.(errorMessage);
            }
          }
        });
      } catch (err) {
        setIsLoading(false);
        const errorMessage = `Failed to initialize YouTube player: ${err}`;
        setError(errorMessage);
        onError?.(errorMessage);
      }
    };

    initPlayer();

    return () => {
      setIsPlayerReady(false);
      if (playerRef.current && playerRef.current.destroy) {
        console.log('🎬 Destroying YouTube player');
        playerRef.current.destroy();
      }
    };
  }, [isAPIReady, videoId, width, height, startTime, endTime, isLooping, loopStartTime, loopEndTime, onReady, onStateChange, onError]);

  // Set up time update interval
  useEffect(() => {
    if (!isPlayerReady || !playerRef.current || !onTimeUpdate) return;

    const updateTime = () => {
      try {
        if (playerRef.current && typeof playerRef.current.getCurrentTime === 'function') {
          const currentTime = playerRef.current.getCurrentTime();
          onTimeUpdate(currentTime);
        }
      } catch (err) {
        console.error('Error getting current time:', err);
      }
    };

    timeUpdateIntervalRef.current = setInterval(updateTime, 100);

    return () => {
      if (timeUpdateIntervalRef.current) {
        clearInterval(timeUpdateIntervalRef.current);
      }
    };
  }, [onTimeUpdate, isPlayerReady]);

  // Player control methods
  const play = useCallback(() => {
    console.log('🎬 YouTubePlayer play() called, playerRef:', playerRef.current);
    if (playerRef.current && playerRef.current.playVideo) {
      console.log('🎬 Calling playVideo()');
      playerRef.current.playVideo();
    } else {
      console.log('🎬 Player not ready or playVideo method not available');
    }
  }, []);

  const pause = useCallback(() => {
    if (playerRef.current && playerRef.current.pauseVideo) {
      playerRef.current.pauseVideo();
    }
  }, []);

  const seekTo = useCallback((time: number) => {
    console.log('🎬 YouTubePlayer seekTo() called:', time);
    if (playerRef.current && playerRef.current.seekTo) {
      console.log('🎬 Calling seekTo()');
      playerRef.current.seekTo(time);
      // Call onSeek without dependency to avoid re-render loops
      if (onSeek) {
        onSeek(time);
      }
    } else {
      console.log('🎬 Player not ready or seekTo method not available');
    }
  }, []); // Removed onSeek dependency

  const getCurrentTime = useCallback(() => {
    if (playerRef.current && playerRef.current.getCurrentTime) {
      return playerRef.current.getCurrentTime();
    }
    return 0;
  }, []);

  const getDuration = useCallback(() => {
    if (playerRef.current && playerRef.current.getDuration) {
      return playerRef.current.getDuration();
    }
    return 0;
  }, []);

  const getPlayerState = useCallback(() => {
    if (playerRef.current && playerRef.current.getPlayerState) {
      return playerRef.current.getPlayerState();
    }
    return -1; // Unstarted
  }, []);

  const setPlaybackRate = useCallback((rate: number) => {
    if (playerRef.current && playerRef.current.setPlaybackRate) {
      try {
        playerRef.current.setPlaybackRate(rate);
        console.log('🎬 Set playback rate to:', rate);
      } catch (error) {
        console.error('🎬 Error setting playback rate:', error);
      }
    }
  }, []);

  const getAvailablePlaybackRates = useCallback(() => {
    if (playerRef.current && playerRef.current.getAvailablePlaybackRates) {
      try {
        return playerRef.current.getAvailablePlaybackRates();
      } catch (error) {
        console.error('🎬 Error getting available playback rates:', error);
        return [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2]; // Default fallback
      }
    }
    return [0.25, 0.5, 0.75, 1, 1.25, 1.5, 2]; // Default fallback
  }, []);

  // Expose player methods to parent component
  useEffect(() => {
    if (onReady && playerRef.current) {
      console.log('🎬 Exposing player methods to parent');
      const playerWithMethods = {
        ...playerRef.current,
        play,
        pause,
        seekTo,
        getCurrentTime,
        getDuration,
        getPlayerState,
        setPlaybackRate,
        getAvailablePlaybackRates
      };
      onReady(playerWithMethods);
    }
  }, [onReady]); // Simplified dependencies

  if (error) {
    return (
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        width={width}
        height={height}
        bgcolor="grey.100"
        borderRadius={1}
      >
        <Typography color="error" textAlign="center">
          {error}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      position="relative"
      sx={{ width: width === '100%' ? '100%' : width, height: height }}
      role="region"
      aria-label="YouTube video player"
      tabIndex={0}
      data-testid="youtube-player-container"
    >
      {isLoading && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          bottom={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
          bgcolor="rgba(0,0,0,0.1)"
          zIndex={1}
        >
          <CircularProgress />
          <Typography sx={{ position: 'absolute', clip: 'rect(0 0 0 0)' }}>
            Loading YouTube player...
          </Typography>
        </Box>
      )}
      <div 
        className={isFlipped ? 'youtube-flipped' : 'youtube-normal'}
        style={{ 
          width: '100%', 
          height: '100%',
          position: 'relative'
        }}
      >
        <div 
          ref={containerRef} 
          style={{ 
            width: '100%', 
            height: '100%'
          }} 
        />
      </div>
    </Box>
  );
};

export default YouTubePlayer;
